import type { FeatureLike } from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import LineString from 'ol/geom/LineString';
import MultiPoint from 'ol/geom/MultiPoint';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import { Circle as CircleStyle, Fill, RegularShape, Stroke, Style, Text } from 'ol/style';
import type { PlanningObjectMetadata, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { planningLabelParts, planningPreset, type PlanningPresetId } from '../domain/planningObjects';
import { resolvePlanningAppearance, type ResolvedAppearance } from '../domain/planningAppearance';
import { locationIcon, locationSymbolMetrics, type LocationSymbolId } from './locationSymbols';
import { PUMP_SYMBOL_SIZE, pumpIcon } from './pumpSymbol';
import { WELL_SYMBOL_SIZE, wellIcon } from './wellSymbol';

/**
 * Map symbology of planning objects. It conveys the technical intent: a pipe is a cased double stroke with round
 * caps (dashed core when proposed), unlike a canal (water band), a drain (dashed green), a trench (ticked brown) or
 * a route (grey dashes). Point presets have their own shape (letter or drawing); polygons their fill, outline and
 * hatching. The object's «Apariencia» (V1.8) adjusts size, opacity, widths, patterns, fill and label; renderScale
 * then scales everything once, so the PNG export draws exactly the same composition.
 */

const rgba = (hex: string, alpha: number): string => {
  if (alpha >= 1 && /^#[0-9a-f]{6}$/i.test(hex)) return hex;
  const value = hex.replace('#', ''); const full = value.length === 3 ? value.split('').map((char) => char + char).join('') : value.padEnd(6, '0');
  const [r, g, b] = [0, 2, 4].map((index) => parseInt(full.slice(index, index + 2), 16) || 0);
  return `rgba(${r},${g},${b},${alpha})`;
};
const proposed = (status: string): boolean => status !== '' && status !== 'Existente';
/** White with an alpha: outlines and cores follow the object's opacity. */
const white = (alpha: number): string => alpha >= 1 ? '#ffffff' : `rgba(255,255,255,${alpha})`;

/** Diagonal hatching for restricted zones; plain translucent colour where no canvas is available (tests, workers). */
const hatchCache = new Map<string, CanvasPattern | string>();
function hatch(color: string, scale: number, strength = 0.22): CanvasPattern | string {
  const key = `${color}:${scale}:${strength}`; const cached = hatchCache.get(key); if (cached) return cached;
  let pattern: CanvasPattern | string = rgba(color, strength);
  try {
    const size = Math.max(6, Math.round(10 * scale)); const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
    const context = canvas.getContext('2d');
    if (context) {
      context.fillStyle = rgba(color, strength * 0.45); context.fillRect(0, 0, size, size);
      context.strokeStyle = rgba(color, Math.min(1, strength * 3.4)); context.lineWidth = Math.max(1, 1.4 * scale);
      context.beginPath(); context.moveTo(0, size); context.lineTo(size, 0); context.moveTo(-size / 2, size / 2); context.lineTo(size / 2, -size / 2); context.moveTo(size / 2, size * 1.5); context.lineTo(size * 1.5, size / 2); context.stroke();
      pattern = context.createPattern(canvas, 'repeat') ?? pattern;
    }
  } catch { /* fallback colour */ }
  hatchCache.set(key, pattern);
  return pattern;
}

/** Letters of the conventional point symbols. The «Noria / pozo» has its own drawing (no «N» since V1.8). */
const POINT_GLYPH: Readonly<Partial<Record<PlanningPresetId, string>>> = { hydrant: 'H', valve: 'V', pump: 'B', 'point-obstacle': '!' };

/** Half the height of a point symbol on screen (the label goes below it), at symbol size `size`. */
export function pointSymbolHalfHeight(presetId: PlanningPresetId, size: number): number {
  if (presetId === 'gps-point' || presetId === 'destination') return locationSymbolMetrics(presetId).below * size;
  if (presetId === 'farm') return 13 * size;
  if (presetId === 'text') return 0;
  return (presetId === 'pump' ? PUMP_SYMBOL_SIZE.height / 2 : presetId === 'well' ? WELL_SYMBOL_SIZE / 2 : 11) * size;
}

/** `size` = symbol scale × render scale (the only scaling of a point symbol); `opacity` 0.1–1. */
function pointSymbol(presetId: PlanningPresetId, color: string, status: string, size: number, opacity: number, variant: ResolvedAppearance['symbol']['variant']): Style[] {
  const dash = proposed(status) ? [3 * size, 2 * size] : undefined;
  const tone = rgba(color, opacity);
  const outline = new Stroke({ color: white(opacity), width: 2 * size, lineDash: dash });
  const glyph = POINT_GLYPH[presetId];
  const letter = glyph ? new Text({ declutterMode: 'none', text: glyph, font: `800 ${10 * size}px Inter, system-ui, sans-serif`, fill: new Fill({ color: white(opacity) }), offsetY: presetId === 'point-obstacle' ? 2 * size : 0.5 * size }) : undefined;
  if (presetId === 'well') {
    const icon = wellIcon(variant, color, proposed(status), size, opacity);
    if (icon) return [new Style({ image: icon })];
    // No 2D canvas (tests): a ring, still without a letter.
    return [new Style({ image: new CircleStyle({ declutterMode: 'obstacle', radius: 10 * size, fill: new Fill({ color: variant === 'drilled' ? 'rgba(212,207,194,.95)' : 'rgba(155,144,128,.95)' }), stroke: new Stroke({ color: tone, width: 3 * size, lineDash: dash }) }) })];
  }
  if (presetId === 'text') return [];
  if (presetId === 'gps-point' || presetId === 'farm' || presetId === 'destination') {
    const icon = locationIcon(presetId as LocationSymbolId, color, variant, size, opacity);
    if (icon) return [new Style({ image: icon })];
    return [new Style({ image: new CircleStyle({ declutterMode: 'obstacle', radius: 8 * size, fill: new Fill({ color: tone }), stroke: outline }) })];
  }
  if (presetId === 'valve') return [new Style({ image: new RegularShape({ declutterMode: 'obstacle', points: 4, radius: 10 * size, angle: 0, fill: new Fill({ color: tone }), stroke: outline }), text: letter })];
  if (presetId === 'pump') {
    const icon = pumpIcon(color, proposed(status), size, opacity);
    return [icon ? new Style({ image: icon }) : new Style({ image: new RegularShape({ declutterMode: 'obstacle', points: 4, radius: 10 * size, angle: Math.PI / 4, fill: new Fill({ color: tone }), stroke: outline }), text: letter })];
  }
  if (presetId === 'point-obstacle') return [new Style({ image: new RegularShape({ declutterMode: 'obstacle', points: 3, radius: 11 * size, angle: 0, fill: new Fill({ color: tone }), stroke: outline }), text: letter })];
  if (presetId === 'marker') return [new Style({ image: new RegularShape({ declutterMode: 'obstacle', points: 5, radius: 10 * size, radius2: 4.5 * size, angle: 0, fill: new Fill({ color: tone }), stroke: new Stroke({ color: white(opacity), width: 1.5 * size }) }) })];
  if (presetId === 'connection') return [new Style({ image: new CircleStyle({ declutterMode: 'obstacle', radius: 5.5 * size, fill: new Fill({ color: white(opacity) }), stroke: new Stroke({ color: tone, width: 3 * size, lineDash: dash }) }) })];
  // Hidrante: filled circle with H.
  return [new Style({ image: new CircleStyle({ declutterMode: 'obstacle', radius: 9 * size, fill: new Fill({ color: tone }), stroke: outline }), text: letter })];
}

/** Arrowheads along the line every ~140 px (on screen), pointing as drawn or reversed. */
function directionArrows(line: LineString, color: string, reverse: boolean, scale: number, resolution: number, size = 6, edge = '#ffffff', z = 3): Style[] {
  const coordinates = line.getCoordinates(); if (coordinates.length < 2) return [];
  const length = line.getLength(); const spacing = 140 * resolution;
  const count = Math.max(1, Math.min(12, Math.floor(length / spacing)));
  const styles: Style[] = [];
  for (let index = 1; index <= count; index += 1) {
    const fraction = index / (count + 1); const at = line.getCoordinateAt(fraction);
    const before = line.getCoordinateAt(Math.max(0, fraction - 0.001)); const after = line.getCoordinateAt(Math.min(1, fraction + 0.001));
    let angle = Math.atan2(after[1]! - before[1]!, after[0]! - before[0]!); if (reverse) angle += Math.PI;
    styles.push(new Style({ geometry: new Point(at), image: new RegularShape({ declutterMode: 'none', points: 3, radius: size * scale, rotation: Math.PI / 2 - angle, rotateWithView: true, fill: new Fill({ color }), stroke: new Stroke({ color: edge, width: 1.2 * scale }) }), zIndex: z }));
  }
  return styles;
}

/** Big arrowhead at the end of a «Flecha de sentido» (or at its start when reversed). */
function endArrow(line: LineString, color: string, reverse: boolean, scale: number): Style | null {
  const coordinates = line.getCoordinates(); if (coordinates.length < 2) return null;
  const [tip, from] = reverse ? [coordinates[0]!, coordinates[1]!] : [coordinates.at(-1)!, coordinates.at(-2)!];
  const angle = Math.atan2(tip[1]! - from[1]!, tip[0]! - from[0]!);
  return new Style({ geometry: new Point(tip), image: new RegularShape({ declutterMode: 'none', points: 3, radius: 11 * scale, rotation: Math.PI / 2 - angle, rotateWithView: true, fill: new Fill({ color }), stroke: new Stroke({ color: '#ffffff', width: 1.5 * scale }) }), zIndex: 4 });
}

/**
 * V1.9.3 «Ruta de acceso / Carretera»: drawn like the roads of the Híbrido basemap — a dark casing, a pavement body
 * (the object's colour) and an optional centre line, solid or dashed, all following the vertices with round joins.
 * Widths are screen pixels (they scale with «Grosor»); arrows are optional and follow «Sentido».
 */
function roadSymbol(color: string, direction: string, scale: number, resolution: number, line: LineString | null, stroke: ResolvedAppearance['stroke'], road: ResolvedAppearance['road']): Style[] {
  const w = scale * stroke.width; const round = { lineCap: 'round' as CanvasLineCap, lineJoin: 'round' as CanvasLineJoin };
  const styles: Style[] = [
    new Style({ stroke: new Stroke({ color: rgba(road.casing, stroke.opacity), width: 10 * w, ...round }), zIndex: 1 }),
    new Style({ stroke: new Stroke({ color: rgba(color, stroke.opacity), width: 6.6 * w, ...round }), zIndex: 2 }),
  ];
  if (road.center !== 'none') styles.push(new Style({ stroke: new Stroke({ color: rgba(road.centerColor, stroke.opacity), width: 1.3 * w, lineCap: 'butt', lineJoin: 'round', lineDash: road.center === 'dashed' ? [6 * w, 5 * w] : undefined }), zIndex: 3 }));
  if (line && road.arrows && (direction === 'Según el dibujo' || direction === 'Inverso al dibujo')) styles.push(...directionArrows(line, rgba(road.centerColor, stroke.opacity), direction === 'Inverso al dibujo', Math.max(scale, w), resolution, 5.2, road.casing, 5));
  return styles;
}

/**
 * Line symbology. The appearance scales the width (keeping the casing/core proportions of a pipe), sets the opacity
 * and can force a solid or dashed pattern; «auto» keeps the technical pattern of the type (proposed = dashed). The
 * arrows of «Sentido» and of the «Flecha de sentido» are always drawn: no style removes the direction.
 */
function lineSymbol(presetId: PlanningPresetId, color: string, status: string, direction: string, scale: number, resolution: number, line: LineString | null, stroke: ResolvedAppearance['stroke'], road: ResolvedAppearance['road']): Style[] {
  if (presetId === 'access-road') return roadSymbol(color, direction, scale, resolution, line, stroke, road);
  const w = scale * stroke.width; const tone = rgba(color, stroke.opacity);
  const dashed = stroke.pattern === 'dashed' || (stroke.pattern === 'auto' && proposed(status));
  const typeDash = (dash: number[]): number[] | undefined => stroke.pattern === 'solid' ? undefined : stroke.pattern === 'dashed' ? [10 * w, 5 * w] : dash.map((value) => value * w);
  const round = { lineCap: 'round' as CanvasLineCap, lineJoin: 'round' as CanvasLineJoin };
  const pipe = (casing: number, core: number): Style[] => [
    new Style({ stroke: new Stroke({ color: tone, width: casing * w, ...round }), zIndex: 1 }),
    new Style({ stroke: new Stroke({ color: white(stroke.opacity), width: core * w, ...round, lineDash: dashed ? [8 * w, 5 * w] : undefined }), zIndex: 2 }),
  ];
  let styles: Style[];
  if (presetId === 'conduction-pipe') styles = pipe(7, 3.2);
  else if (presetId === 'main-pipe') styles = pipe(9, 4.2);
  else if (presetId === 'secondary-pipe') styles = pipe(5.5, 2.2);
  else if (presetId === 'canal') styles = [
    new Style({ stroke: new Stroke({ color: rgba(color, 0.35 * stroke.opacity), width: 9 * w, ...round }), zIndex: 1 }),
    new Style({ stroke: new Stroke({ color: tone, width: 3 * w, ...round, lineDash: dashed ? [12 * w, 6 * w] : undefined }), zIndex: 2 }),
  ];
  else if (presetId === 'drain') styles = [new Style({ stroke: new Stroke({ color: tone, width: 3 * w, lineCap: 'butt', lineDash: typeDash([10, 4, 2, 4]) }), zIndex: 1 })];
  else if (presetId === 'trench') styles = [
    new Style({ stroke: new Stroke({ color: tone, width: 2 * w, ...round, lineDash: stroke.pattern === 'dashed' ? [10 * w, 5 * w] : undefined }), zIndex: 1 }),
    new Style({ stroke: new Stroke({ color: tone, width: 9 * w, lineCap: 'butt', lineDash: [2 * w, 6 * w] }), zIndex: 2 }),
  ];
  else if (presetId === 'route') styles = [new Style({ stroke: new Stroke({ color: tone, width: 3.5 * w, lineCap: 'butt', lineDash: typeDash([14, 7]) }), zIndex: 1 })];
  else styles = [new Style({ stroke: new Stroke({ color: tone, width: 3.5 * w, ...round, lineDash: dashed ? [10 * w, 5 * w] : undefined }), zIndex: 1 })];
  if (line && presetId === 'direction-arrow') { const arrow = endArrow(line, tone, direction === 'Inverso al dibujo', Math.max(scale, w)); if (arrow) styles.push(arrow); }
  else if (line && (direction === 'Según el dibujo' || direction === 'Inverso al dibujo')) styles.push(...directionArrows(line, tone, direction === 'Inverso al dibujo', Math.max(scale, w), resolution, presetId.endsWith('pipe') ? 5.5 : 5));
  return styles;
}

/** Polygon symbology: fill (or none), its colour and opacity (hatched for restricted zones), outline width/opacity/pattern. */
function polygonSymbol(presetId: PlanningPresetId, color: string, status: string, scale: number, appearance: ResolvedAppearance): Style[] {
  const { fill, stroke } = appearance; const w = scale * stroke.width;
  const dash = stroke.pattern === 'solid' ? undefined : stroke.pattern === 'dashed' ? [9 * w, 5 * w] : presetId === 'coverage-area' ? [2 * w, 4 * w] : proposed(status) ? [9 * w, 5 * w] : undefined;
  const fillColor = fill.color ?? color;
  const fillStyle = !fill.visible ? undefined : new Fill({ color: presetId === 'restricted-zone' ? hatch(fillColor, scale, fill.opacity) : rgba(fillColor, fill.opacity) });
  return [new Style({ fill: fillStyle, stroke: new Stroke({ color: rgba(color, stroke.opacity), width: (presetId === 'restricted-zone' ? 2.5 : 2) * w, lineDash: dash, lineJoin: 'round' }), zIndex: 1 })];
}

/** Display-only wrapping of long label lines (the stored text is unchanged): ~40 characters per line, at spaces. */
export function wrapLabel(text: string, width = 40): string {
  return text.split('\n').flatMap((line) => {
    if (line.length <= width) return [line];
    const out: string[] = []; let current = '';
    line.split(' ').forEach((word) => { if (current && (current + ' ' + word).length > width) { out.push(current); current = word; } else current = current ? `${current} ${word}` : word; });
    if (current) out.push(current);
    return out;
  }).join('\n');
}

const DEGREE = Math.PI / 180;
type Vector = readonly [number, number];
/** Screen rotation (canvas: +y down, positive = clockwise). */
const rotate = ([x, y]: Vector, angle: number): Vector => angle ? [x * Math.cos(angle) - y * Math.sin(angle), x * Math.sin(angle) + y * Math.cos(angle)] : [x, y];

/**
 * V1.9.0 E4 · screen angle (radians, clockwise) of a route at its label anchor (the middle of its length), kept
 * readable: between −90° and 90°, so the text never reads upside down. The route itself is never touched.
 */
export function lineLabelAngle(line: LineString): number {
  const length = line.getLength(); if (!(length > 0)) return 0;
  const before = line.getCoordinateAt(0.49); const after = line.getCoordinateAt(0.51); // the segment under the label
  let angle = -Math.atan2(after[1]! - before[1]!, after[0]! - before[0]!); // map +y is up, screen +y is down
  // Readable: −90°…90° (a vertical route reads bottom to top, the cartographic convention).
  if (angle >= Math.PI / 2 - 1e-9) angle -= Math.PI; else if (angle < -Math.PI / 2 - 1e-9) angle += Math.PI;
  return Math.abs(angle) < 1e-9 ? 0 : angle;
}

/** Text block size on screen (estimate used only to place the measure beside the label). */
const textBox = (text: string, size: number, bold: boolean): Vector => {
  const lines = text ? text.split('\n') : [];
  return [Math.max(0, ...lines.map((line) => line.length)) * size * (bold ? 0.6 : 0.56), lines.length * size * 1.25];
};

interface LabelLayout {
  /** Text anchor relative to the object's anchor, in screen px. */
  readonly anchor: Vector;
  /** Centre of the text block, in screen px. */
  readonly centre: Vector;
  /** Centre of the text block at its automatic position (no user offset). */
  readonly autoCentre: Vector;
  readonly angle: number;
  readonly size: Vector;
}

/**
 * Where the label goes: below a point (clear of its symbol), above the middle of a line (by length; with «seguir el
 * recorrido» along it) or at the interior point of a polygon — always from the current geometry — then shifted by the
 * user's offset. Offsets are screen pixels at any angle (OpenLayers rotates a text offset with the text, so it is
 * converted to the text's frame), hence «Mover en el mapa» moves the label exactly with the pointer.
 */
function labelLayout(text: string, geometry: Geometry, scale: number, label: ResolvedAppearance['label'], pointOffset: number, centered = false): LabelLayout {
  const type = geometry.getType();
  const angle = label.orientation === 'line' && geometry instanceof LineString ? lineLabelAngle(geometry) : (label.rotation % 360) * DEGREE;
  const [width, height] = textBox(wrapLabel(text), label.size * scale, label.bold);
  // Automatic position: a line label sits along the route (in the text's frame); a point label below its symbol.
  const base: Vector = type === 'LineString' ? rotate([0, -9 * scale], angle) : type === 'Point' && !centered ? [0, pointOffset * scale] : [0, 0];
  const toCentre = rotate([0, type === 'Point' && !centered ? height / 2 : type === 'LineString' ? -height / 2 : 0], angle);
  const anchor: Vector = [base[0] + label.offsetX * scale, base[1] + label.offsetY * scale];
  return { anchor, angle, size: [width, height], centre: [anchor[0] + toCentre[0], anchor[1] + toCentre[1]], autoCentre: [base[0] + toCentre[0], base[1] + toCentre[1]] };
}

const labelAnchor = (geometry: Geometry): Point | undefined => {
  const type = geometry.getType();
  return type === 'LineString' ? new Point((geometry as LineString).getCoordinateAt(0.5)) : type === 'Polygon' ? (geometry as Polygon).getInteriorPoint() : undefined;
};

const textHalo = (label: ResolvedAppearance['label'], scale: number) => ({
  stroke: label.halo ? new Stroke({ color: rgba(label.haloColor, 0.95), width: 3.4 * scale, lineJoin: 'round' as const }) : undefined,
  backgroundFill: label.background ? new Fill({ color: rgba(label.backgroundColor, label.backgroundOpacity) }) : undefined,
  padding: label.background ? [2 * scale, 4 * scale, 1 * scale, 4 * scale] : undefined,
});

/**
 * Label of a planning object (see labelLayout). Everything (font, halo, padding, offsets) scales once by renderScale;
 * the angle does not. No box unless the object asks for one: dark text with a light halo by default.
 */
const NOTE_FONTS: Readonly<Record<ResolvedAppearance['note']['font'], string>> = { sans: 'Inter, system-ui, sans-serif', serif: 'Georgia, "Times New Roman", serif', mono: '"Cascadia Mono", Consolas, "Courier New", monospace' };

/** «Insertar texto»: the text is centred on its point (no symbol under it), with its alignment, font and border. */
export interface NoteLook { readonly note: ResolvedAppearance['note'] }

export function planningLabelStyle(text: string, geometry: Geometry, scale: number, label: ResolvedAppearance['label'], pointOffset = 14, paired = false, look?: NoteLook): Style {
  const type = geometry.getType(); const layout = labelLayout(text, geometry, scale, label, pointOffset, Boolean(look));
  const offset = rotate(layout.anchor, -layout.angle); const note = look?.note;
  const halo = textHalo(label, scale);
  const border = note?.border ? new Stroke({ color: note.borderColor, width: note.borderWidth * scale, lineJoin: 'round' as const }) : undefined;
  return new Style({
    geometry: labelAnchor(geometry),
    text: new Text({
      text: look ? text : wrapLabel(text), font: `${label.bold ? 650 : 450} ${label.size * scale}px ${note ? NOTE_FONTS[note.font] : 'Inter, system-ui, sans-serif'}`, textAlign: note?.align ?? 'center', textBaseline: look ? 'middle' : type === 'Point' ? 'top' : type === 'LineString' ? 'bottom' : 'middle',
      rotation: layout.angle, offsetX: offset[0], offsetY: offset[1], ...(paired ? { declutterMode: 'obstacle' as const } : {}), ...(look ? { declutterMode: 'none' as const } : {}),
      fill: new Fill({ color: label.color }), ...halo, ...(border ? { backgroundStroke: border, padding: halo.padding ?? [3 * scale, 5 * scale, 2 * scale, 5 * scale] } : {}),
      overflow: true,
    }),
    zIndex: 50,
  });
}

/**
 * Call-out of a free text moved away from its point of interest: a thin line from the point to the edge of the text
 * box plus a small dot on the point. Computed from the same layout as the text, so it follows the text, its angle and
 * its offset in the map and in the PNG. Nothing is drawn while the text sits on its point.
 */
export function noteLeaderStyles(text: string, geometry: Geometry, scale: number, resolution: number, label: ResolvedAppearance['label'], note: ResolvedAppearance['note']): Style[] {
  if (!note.leader || geometry.getType() !== 'Point') return [];
  const layout = labelLayout(text, geometry, scale, label, 0, true);
  const [cx, cy] = rotate(layout.centre, -layout.angle); if (!cx && !cy) return [];
  const [width, height] = layout.size; const pad = 6 * scale; const hw = width / 2 + pad; const hh = height / 2 + pad;
  const ratio = Math.min(Math.abs(cx) > 1e-6 ? hw / Math.abs(cx) : Infinity, Math.abs(cy) > 1e-6 ? hh / Math.abs(cy) : Infinity);
  if (ratio >= 1) return []; // the point is inside the text box
  const [ex, ey] = rotate([cx * (1 - ratio), cy * (1 - ratio)], layout.angle);
  const origin = (geometry as Point).getCoordinates();
  const colour = note.borderColor;
  return [
    new Style({ geometry: new LineString([origin, [origin[0]! + ex * resolution, origin[1]! - ey * resolution]]), stroke: new Stroke({ color: '#ffffff', width: 3.2 * scale, lineCap: 'round' }), zIndex: 47 }),
    new Style({ geometry: new LineString([origin, [origin[0]! + ex * resolution, origin[1]! - ey * resolution]]), stroke: new Stroke({ color: colour, width: 1.4 * scale, lineCap: 'round' }), zIndex: 48 }),
    new Style({ geometry: new Point(origin), image: new CircleStyle({ declutterMode: 'none', radius: 3.2 * scale, fill: new Fill({ color: colour }), stroke: new Stroke({ color: '#ffffff', width: 1.4 * scale }) }), zIndex: 49 }),
  ];
}

/**
 * V1.9.0 E4 · the measure as its own text: beside the label (above, below, left, right — in the label's frame, so it
 * stays beside a rotated label) or «libre» (from the label's automatic position), plus its own offset, size, colour and
 * angle. It keeps the label's halo and background so both read alike.
 * Label and measure are drawn as a pair: decluttering each on its own could hide the label and leave its measure
 * floating, so both are «obstacles» — always drawn, and other labels make way for them.
 */
export function planningMeasureStyle(measure: string, labelText: string, geometry: Geometry, scale: number, appearance: Pick<ResolvedAppearance, 'label' | 'measure'>, pointOffset = 14): Style {
  const m = appearance.measure; const layout = labelLayout(labelText, geometry, scale, appearance.label, pointOffset);
  const angle = m.orientation === 'line' && geometry instanceof LineString ? lineLabelAngle(geometry) : m.orientation === 'manual' ? (m.rotation % 360) * DEGREE : layout.angle;
  const [width, height] = textBox(measure, m.size * scale, m.bold); const gap = 3 * scale;
  const [labelWidth, labelHeight] = labelText ? layout.size : [0, 0];
  const beside: Vector = m.placement === 'above' ? [0, -(labelHeight / 2 + gap + height / 2)] : m.placement === 'below' ? [0, labelHeight / 2 + gap + height / 2]
    : m.placement === 'left' ? [-(labelWidth / 2 + gap + width / 2), 0] : m.placement === 'right' ? [labelWidth / 2 + gap + width / 2, 0] : [0, 0];
  const from = m.placement === 'free' || !labelText ? layout.autoCentre : layout.centre;
  const shift = rotate(beside, layout.angle);
  const centre: Vector = [from[0] + shift[0] + m.offsetX * scale, from[1] + shift[1] + m.offsetY * scale];
  const offset = rotate(centre, -angle);
  return new Style({
    geometry: labelAnchor(geometry),
    text: new Text({
      text: measure, font: `${m.bold ? 650 : 450} ${m.size * scale}px Inter, system-ui, sans-serif`, textAlign: 'center', textBaseline: 'middle',
      rotation: angle, offsetX: offset[0], offsetY: offset[1], declutterMode: 'obstacle',
      fill: new Fill({ color: m.color }), ...textHalo(appearance.label, scale),
      overflow: true,
    }),
    zIndex: 51,
  });
}

function selectionHalo(geometry: Geometry, scale: number, pointRadius = 15, displacement: readonly [number, number] = [0, 0]): Style {
  const halo = '#ffb000';
  return geometry.getType() === 'Point'
    ? new Style({ image: new CircleStyle({ declutterMode: 'obstacle', radius: pointRadius * scale, displacement: [displacement[0] * scale, displacement[1] * scale], fill: new Fill({ color: 'rgba(255,176,0,.28)' }), stroke: new Stroke({ color: halo, width: 2.5 * scale }) }), zIndex: 9 })
    : new Style({ stroke: new Stroke({ color: 'rgba(255,176,0,.75)', width: (geometry.getType() === 'LineString' ? 15 : 5) * scale, lineCap: 'round', lineJoin: 'round' }), fill: geometry.getType() === 'Polygon' ? new Fill({ color: 'rgba(255,211,90,.25)' }) : undefined, zIndex: 0 });
}

/** Selection halo of the point symbols that are not centred on their point (the pin and the flag stand on it). */
const HALO_BY_PRESET: Readonly<Partial<Record<PlanningPresetId, { readonly radius: number; readonly displacement: readonly [number, number] }>>> = {
  'gps-point': { radius: 17, displacement: [0, 15] }, destination: { radius: 19, displacement: [9, 17] }, farm: { radius: 19, displacement: [0, 0] }, text: { radius: 9, displacement: [0, 0] },
};

/** Full style of one planning object; zIndex inside the layer keeps halo < symbol < arrows < label. */
export function planningObjectStyle(record: VectorFeatureRecord | undefined, feature: FeatureLike, selected: boolean, renderScale = 1, resolution = 1, showLabels = true): Style[] {
  const geometry = (record?.feature.getGeometry() ?? feature.getGeometry()) as Geometry | undefined;
  const meta = record?.projectMetadata?.planning;
  if (!geometry || !meta) return [];
  const preset = planningPreset(meta.preset, record.projectMetadata!.geometryType);
  const appearance = resolvePlanningAppearance(meta);
  const color = meta.color ?? preset.color;
  const status = String(record.attributes.Estado ?? ''); const direction = String(record.attributes.Sentido ?? '');
  const symbolSize = renderScale * appearance.symbol.scale;
  const half = pointSymbolHalfHeight(preset.id, 1);
  const halo = HALO_BY_PRESET[preset.id];
  const styles: Style[] = selected ? [selectionHalo(geometry, preset.geometry === 'Point' ? symbolSize : renderScale, preset.geometry === 'Point' ? halo?.radius ?? Math.max(15, (preset.id === 'pump' ? PUMP_SYMBOL_SIZE.width / 2 : half) + 4) : 15, halo?.displacement)] : [];
  // Points are drawn (and hit-tested) above lines and polygons, so a hydrant at the start of a pipe stays clickable.
  if (preset.geometry === 'Point') styles.push(...pointSymbol(preset.id, color, status, symbolSize, appearance.symbol.opacity, appearance.symbol.variant).map((style) => { style.setZIndex(10 + (style.getZIndex() ?? 0)); return style; }));
  else if (preset.geometry === 'LineString') styles.push(...lineSymbol(preset.id, color, status, direction, renderScale, resolution, geometry instanceof LineString ? geometry : null, appearance.stroke, appearance.road));
  else styles.push(...polygonSymbol(preset.id, color, status, renderScale, appearance));
  const { text, measure } = showLabels ? planningLabelParts(record) : { text: '', measure: '' };
  // The label clears the symbol at its own size (a smaller pump brings the label closer; the offset is added on top).
  const pointOffset = preset.geometry === 'Point' ? half * appearance.symbol.scale + 3 : 14;
  if (preset.id === 'text') {
    if (text) { styles.push(...noteLeaderStyles(text, geometry, renderScale, resolution, appearance.label, appearance.note)); styles.push(planningLabelStyle(text, geometry, renderScale, appearance.label, 0, false, { note: appearance.note })); }
    return styles;
  }
  if (text) styles.push(planningLabelStyle(text, geometry, renderScale, appearance.label, pointOffset, Boolean(measure)));
  if (measure) styles.push(planningMeasureStyle(measure, text, geometry, renderScale, appearance, pointOffset));
  return styles;
}

export interface PlanningLayerStyleOptions {
  /** «Etiquetas» of the PNG exporter (true on screen): symbols and geometries are always drawn. */
  readonly showLabels?: boolean;
  /** Object whose geometry is being edited: the edit preview draws it instead. */
  readonly hiddenFeatureId?: string | null;
  /**
   * «Apariencia» being edited (not applied): that object is drawn with these settings. Read at render time, so the
   * preview follows every slider step with a repaint of this layer only — no store update, no Maestro redraw.
   */
  readonly preview?: () => PlanningAppearancePreview | null;
}

export interface PlanningAppearancePreview { readonly layerId: string; readonly featureId: string; readonly planning: PlanningObjectMetadata }

/** Style function of a planning layer (same selection contract as genericVectorStyle). */
export const planningLayerStyle = (layer: VectorLayerModel, selectedFeatureIds: ReadonlySet<string>, renderScale = 1, options: PlanningLayerStyleOptions = {}) => (feature: FeatureLike, resolution: number): Style[] => {
  const featureId = String(feature.get('genericFeatureId'));
  if (options.hiddenFeatureId && featureId === options.hiddenFeatureId) return [];
  const record = layer.featuresById.get(featureId); const preview = options.preview?.();
  const drawn = record && preview && preview.layerId === layer.id && preview.featureId === featureId ? { ...record, projectMetadata: { ...record.projectMetadata!, planning: preview.planning } } : record;
  return planningObjectStyle(drawn, feature, selectedFeatureIds.has(featureId), renderScale, resolution, options.showLabels ?? true);
};

const vertexMarker = new CircleStyle({ radius: 5, fill: new Fill({ color: '#ffffff' }), stroke: new Stroke({ color: '#ff00a8', width: 2 }) });

/**
 * Edit preview: the object's own symbology and live label (length/area follow the edited geometry) drawn from the
 * edited clone, with the selection halo and a handle on every vertex so they can be found on touch screens.
 */
export const planningEditStyle = (record: VectorFeatureRecord) => (feature: FeatureLike, resolution: number): Style[] => {
  const geometry = feature.getGeometry() as Geometry | undefined; if (!geometry) return [];
  const styles = planningObjectStyle({ ...record, feature: feature as VectorFeatureRecord['feature'] }, feature, true, 1, resolution, true);
  const type = geometry.getType();
  if (type === 'LineString' || type === 'Polygon') {
    const coordinates = type === 'LineString' ? (geometry as LineString).getCoordinates() : (geometry as Polygon).getCoordinates()[0]!.slice(0, -1);
    styles.push(new Style({ geometry: new MultiPoint(coordinates), image: vertexMarker, zIndex: 60 }));
  }
  return styles;
};
